// Import model
import { Company } from '../models/Company';

// Import base class serializer
import { ContactSerializer } from './ContactSerializer';

// Import dependency types (models and enums used in properties)
import { IndividualContact } from '../models/IndividualContact';
import { PhoneNumber } from '../models/PhoneNumber';

// Import dependency serializers (for complex types)
import { IndividualContactSerializer } from './IndividualContactSerializer';
import { PhoneNumberSerializer } from './PhoneNumberSerializer';

// Import derived type serializers (for polymorphic deserialization)

/**
 * Serializer for Company - handles JSON serialization and deserialization with type validation
 */
export class CompanySerializer {
  /**
   * Deserializes a JSON string into a Company instance
   */
  static deserialize(json: string): Company {
    const obj = JSON.parse(json);
    return this.fromObject(obj);
  }

  /**
   * Deserializes a plain object into a Company instance
   */
  static fromObject(obj: any): Company {
    if (!obj) {
      throw new Error('Cannot deserialize null or undefined to Company');
    }


    const instance: Company = {
      role: obj['role'] as string | undefined,
      flowId: obj['flowId'] as string | undefined,
      flowIdName: obj['flowId|Name'] as string | undefined,
      objectStatusId: obj['objectStatusId'] as string | undefined,
      objectStatusIdName: obj['objectStatusId|Name'] as string | undefined,
      tags: obj['tags'] ? (obj['tags'] as any[]).map((item: any) =>
        item as string
      ) : [] as any,
      address: obj['address'] as string | undefined,
      city: obj['city'] as string | undefined,
      state: obj['state'] as string | undefined,
      postalCode: obj['postalCode'] as string | undefined,
      country: obj['country'] as string | undefined,
      email: obj['email'] as string | undefined,
      location: obj['location'] as any | undefined,
      locationDistance: obj['locationDistance'] as any | undefined,
      creatorId: obj['creatorId'] as string | undefined,
      creatorIdName: obj['creatorId|Name'] as string | undefined,
      parentContactId: obj['parentContactId'] as string | undefined,
      parentContactIdName: obj['parentContactId|Name'] as string | undefined,
      phoneNumbers: obj['phoneNumbers'] ? (obj['phoneNumbers'] as any[]).map((item: any) =>
        PhoneNumberSerializer.fromObject(item)
      ) : [] as any,
      type_: obj['type'] as string,
      name: obj['name'] as string | undefined,
      description: obj['description'] as string | undefined,
      entityId: obj['entityId'] as string | undefined,
      entityIdName: obj['entityId|Name'] as string | undefined,
      id: obj['_id'] as string | undefined,
      createdDate: obj['createdDate'] as string | undefined,
      notes: obj['notes'] as string | undefined,
      address2: obj['address2'] as string | undefined,
      parentContact: obj['parentContact'] ? ContactSerializer.fromObject(obj['parentContact']) : undefined as any,
      d04E1F21: obj['_d04e1f21'] as any | undefined,
      individualContacts: obj['individualContacts'] ? (obj['individualContacts'] as any[]).map((item: any) =>
        IndividualContactSerializer.fromObject(item)
      ) : [] as any,
    };

    return instance;
  }

  /**
   * Deserializes a JSON string into an array of Company instances
   */
  static deserializeArray(json: string): Company[] {
    const arr = JSON.parse(json);
    if (!Array.isArray(arr)) {
      throw new Error('Expected JSON array');
    }
    return arr.map(obj => this.fromObject(obj));
  }

  /**
   * Serializes a Company instance to a JSON string
   */
  static serialize(instance: Company): string {
    return JSON.stringify(this.toObject(instance));
  }

  /**
   * Converts a Company instance to a plain object
   */
  static toObject(instance: Company): any {
    return {
      'role': instance.role,
      'flowId': instance.flowId,
      'flowId|Name': instance.flowIdName,
      'objectStatusId': instance.objectStatusId,
      'objectStatusId|Name': instance.objectStatusIdName,
      'tags': instance.tags?.map((item: any) =>
        item
      ),
      'address': instance.address,
      'city': instance.city,
      'state': instance.state,
      'postalCode': instance.postalCode,
      'country': instance.country,
      'email': instance.email,
      'location': instance.location,
      'locationDistance': instance.locationDistance,
      'creatorId': instance.creatorId,
      'creatorId|Name': instance.creatorIdName,
      'parentContactId': instance.parentContactId,
      'parentContactId|Name': instance.parentContactIdName,
      'phoneNumbers': instance.phoneNumbers?.map((item: any) =>
        PhoneNumberSerializer.toObject(item)
      ),
      'type': instance.type_,
      'name': instance.name,
      'description': instance.description,
      'entityId': instance.entityId,
      'entityId|Name': instance.entityIdName,
      '_id': instance.id,
      'createdDate': instance.createdDate,
      'notes': instance.notes,
      'address2': instance.address2,
      'parentContact': instance.parentContact ? ContactSerializer.toObject(instance.parentContact) : undefined,
      '_d04e1f21': instance.d04E1F21,
      'individualContacts': instance.individualContacts?.map((item: any) =>
        IndividualContactSerializer.toObject(item)
      ),
    };
  }

  /**
   * Serializes an array of Company instances to a JSON string
   */
  static serializeArray(instances: Company[]): string {
    return JSON.stringify(instances.map(inst => this.toObject(inst)));
  }
}
